/**
 * Viewer do documento na cerimônia (#91, D-063 item 1) — o signatário VÊ o que
 * assina. pdf.js com worker bundlado local (`lib/pdf-worker.ts`, side-effect
 * import — nada de CDN), bytes via `GET .../content` com SST (revisão CORRENTE,
 * inclusive assinaturas anteriores numa cerimônia sequencial).
 *
 * Falha de render é ERRO EXPLÍCITO (StateCard honesto + código de suporte +
 * retry) — NUNCA substituímos o documento por hash "equivalente": sem documento
 * em tela não há "li e concordo" verdadeiro (o consentimento fica bloqueado; ver
 * `onLoadState` + SignPage). O hash continua exposto como âncora de integridade
 * (metadado), não como substituto do conteúdo.
 *
 * Carimbo (D-063 item 1): com `visual` presente, um indicador DISCRETO mostra
 * onde o carimbo vai aparecer ("seu carimbo aparecerá aqui") — frações com
 * origem topo-esquerda (D-027), mesma convenção provada no signing-workbench
 * (`stamp-geometry.ts`). Escolha de rubrica/reposicionamento NÃO entra (pendência
 * registrada no fecho da rodada).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import '../lib/pdf-worker';
import { getDocument, type PDFDocumentProxy } from 'pdfjs-dist';
import type { VisualPlacement } from '@argink/shared';
import { Button } from '@argink/ui';
import { getDocumentContent } from '../lib/api';
import { describeError, type HumanError } from '../lib/errors';
import { DraggableStamp } from './DraggableStamp';
import { StateCard } from './StateCard';

const MAX_DISPLAY_WIDTH = 900;

export type ViewerLoadState = 'loading' | 'loaded' | 'error';

export interface PdfViewerProps {
  envelopeId: string;
  documentId: string;
  /** Carimbo configurado para ESTE signatário (SignerView.visual) — `null` = sem indicador. */
  visual: VisualPlacement | null;
  /** Reporta o estado de carga para a página gatear o consentimento (#91). */
  onLoadState: (state: ViewerLoadState) => void;
  /**
   * M5a: com `onVisualChange` + `onVisualCommit`, o carimbo deixa de ser indicador e passa a ser
   * ARRASTÁVEL — o signatário escolhe onde assina, como no GOV.BR. Sem as props, o comportamento
   * é o de antes (indicador passivo), que é o certo depois que a janela de edição fecha.
   *
   * As duas são separadas de propósito: `onVisualChange` acontece a CADA quadro do movimento (é
   * o que desenha o carimbo sob o dedo) e `onVisualCommit` só quando o gesto termina. Ligar as
   * duas na mesma função faria um PUT por pixel arrastado.
   */
  onVisualChange?: (next: VisualPlacement) => void;
  /** Gesto concluído — é aqui que a posição vai para o servidor. */
  onVisualCommit?: (next: VisualPlacement) => void;
  /** Congela o carimbo durante a gravação (ou quando o servidor recusou a janela). */
  visualLocked?: boolean;
}

interface PageBox {
  width: number;
  height: number;
}

export function PdfViewer({
  envelopeId,
  documentId,
  visual,
  onLoadState,
  onVisualChange,
  onVisualCommit,
  visualLocked = false,
}: PdfViewerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null);
  const [error, setError] = useState<HumanError | null>(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageBox, setPageBox] = useState<PageBox | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  /**
   * Muda quando a largura DISPONÍVEL para o documento muda (achado P1 da auditoria). Sem isto o
   * canvas só re-renderizava na troca de página: encolher a janela do desktop para o celular
   * deixava o wrapper com a altura inline antiga, e o carimbo — posicionado em `%` do wrapper —
   * ia parar fora do documento.
   */
  const [widthNonce, setWidthNonce] = useState(0);

  const reportedRef = useRef<ViewerLoadState | null>(null);
  const report = useCallback(
    (state: ViewerLoadState) => {
      if (reportedRef.current === state) return;
      reportedRef.current = state;
      onLoadState(state);
    },
    [onLoadState],
  );

  // Busca os bytes + interpreta o PDF. Recarrega no retry (reloadNonce).
  useEffect(() => {
    let cancelled = false;
    let loadingTask: { destroy: () => Promise<void> } | null = null;
    setLoading(true);
    setError(null);
    setPageBox(null);
    pdfRef.current = null;
    report('loading');

    (async () => {
      const bytes = await getDocumentContent(envelopeId, documentId);
      if (cancelled) return;
      // pdf.js pode transferir/neutralizar o ArrayBuffer passado — cópia defensiva.
      const task = getDocument({ data: bytes.slice(0) });
      loadingTask = task;
      const pdf = await task.promise;
      if (cancelled) return;
      pdfRef.current = pdf;
      setNumPages(pdf.numPages);
      setCurrentPage(1);
      setLoading(false);
      report('loaded');
    })().catch((err: unknown) => {
      if (cancelled) return;
      setLoading(false);
      setError(describeError(err));
      report('error');
    });

    return () => {
      cancelled = true;
      void loadingTask?.destroy().catch(() => undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelopeId, documentId, reloadNonce]);

  /**
   * Observa a largura real disponível. Mede a RAIZ, e não o `canvas-wrap`: o wrap recebe
   * `width`/`height` inline do último render, então medir ele seria perguntar ao passado qual é o
   * tamanho do presente — a leitura nunca encolheria.
   */
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return;
    let ultima = root.clientWidth;
    const observer = new ResizeObserver(() => {
      const atual = root.clientWidth;
      // HISTERESE. Sem ela isto vira um laço de layout: o canvas cresce, a página ganha barra de
      // rolagem, a largura disponível cai ~15px, o canvas re-renderiza menor, a barra some, e
      // recomeça. O limiar é maior que qualquer barra de rolagem e menor que qualquer troca real
      // de viewport. E ele é SEGURO porque a posição da marca não depende deste re-render: quem
      // garante o alinhamento é a proporção fixa do wrapper (ver `canvas-wrap` abaixo) — este
      // efeito existe só para o documento continuar NÍTIDO depois de uma mudança grande.
      if (atual > 0 && Math.abs(atual - ultima) >= 32) {
        ultima = atual;
        setWidthNonce((nonce) => nonce + 1);
      }
    });
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  // Renderiza a página corrente no canvas (dpr-aware), igual ao viewer provado no workbench.
  useEffect(() => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas || loading || error) return;
    if (currentPage < 1 || currentPage > pdf.numPages) return;

    let cancelled = false;
    renderTaskRef.current?.cancel();

    pdf
      .getPage(currentPage)
      .then(async (page) => {
        if (cancelled) return;
        const base = page.getViewport({ scale: 1 });
        // A largura vem da RAIZ (ver o observador acima) — nunca do `canvas-wrap`, que carrega a
        // dimensão inline do render anterior.
        const containerWidth = rootRef.current?.clientWidth ?? MAX_DISPLAY_WIDTH;
        const targetWidth = Math.min(MAX_DISPLAY_WIDTH, containerWidth || MAX_DISPLAY_WIDTH);
        const scale = targetWidth / base.width;
        const viewport = page.getViewport({ scale });
        const dpr = window.devicePixelRatio || 1;

        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('canvas 2D não disponível neste navegador');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task;
        await task.promise;
        if (cancelled) return;
        setPageBox({ width: viewport.width, height: viewport.height });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        if (message.toLowerCase().includes('cancel')) return; // render cancelado — não é erro
        setError(describeError(err instanceof Error ? err : new Error(message)));
        report('error');
      });

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, loading, error, widthNonce]);

  if (error) {
    return (
      <StateCard
        tone="error"
        title="Não foi possível exibir o documento"
        detail={`${error.detail} Sem o documento em tela, a assinatura não continua — o aceite exige ver o que se assina.`}
        traceId={error.traceId}
        action={
          <Button variant="outline" onClick={() => setReloadNonce((nonce) => nonce + 1)}>
            Tentar carregar de novo
          </Button>
        }
      />
    );
  }

  const stampVisible = visual !== null && pageBox !== null && currentPage === visual.page;
  /**
   * Editável só quando quem chamou entregou o `onVisualChange` E existe carimbo para mover. Um
   * signatário sem carimbo configurado não ganha um do nada aqui: quem cria o carimbo é o
   * responsável pelo envelope, e a cerimônia REPOSICIONA o que já existe.
   */
  const stampEditable = onVisualChange !== undefined && onVisualCommit !== undefined && visual !== null;

  return (
    <div className="pdf-viewer" ref={rootRef}>
      {loading ? <p className="pdf-viewer__status">Carregando o documento…</p> : null}
      {/*
        A proporção da página, e não a altura em pixels, é o que fixa o wrapper. Com `height`
        inline o wrapper guardava a altura do render ANTERIOR: encolher de desktop para celular
        deixava o canvas menor (por `max-width`) dentro de um wrapper alto, e a marca —
        posicionada em `%` do wrapper — ia parar fora do documento. Com `aspect-ratio` + `width`
        limitada a 100%, o wrapper acompanha o canvas em qualquer tamanho de tela, sem depender
        de re-renderizar nada.
      */}
      <div
        className="pdf-viewer__canvas-wrap"
        style={
          pageBox
            ? { width: pageBox.width, aspectRatio: `${pageBox.width} / ${pageBox.height}` }
            : undefined
        }
      >
        <canvas ref={canvasRef} className="pdf-viewer__canvas" aria-label={`Página ${currentPage} do documento`} />
        {stampVisible && visual && stampEditable ? (
          <DraggableStamp
            value={visual}
            onChange={onVisualChange}
            onCommit={onVisualCommit}
            label="Sua assinatura"
            disabled={visualLocked}
          />
        ) : null}
        {stampVisible && pageBox && !stampEditable ? (
          <div
            className="pdf-viewer__stamp-hint"
            role="note"
            aria-label="Posição do seu carimbo de assinatura nesta página"
            style={{
              left: `${visual.x * pageBox.width}px`,
              top: `${visual.y * pageBox.height}px`,
              width: `${visual.width * pageBox.width}px`,
              height: `${visual.height * pageBox.height}px`,
            }}
          >
            <span className="pdf-viewer__stamp-hint-label">Seu carimbo aparecerá aqui</span>
          </div>
        ) : null}
      </div>
      {!loading && numPages > 0 ? (
        <nav className="pdf-viewer__nav" aria-label="Navegação de páginas do documento">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          >
            Página anterior
          </Button>
          <span className="pdf-viewer__page-indicator" aria-live="polite">
            Página {currentPage} de {numPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= numPages}
            onClick={() => setCurrentPage((page) => Math.min(numPages, page + 1))}
          >
            Próxima página
          </Button>
        </nav>
      ) : null}
      {visual && !stampVisible && !loading ? (
        <div className="pdf-viewer__stamp-note card__note">
          <p>Seu carimbo de assinatura será aplicado na página {visual.page}.</p>
          {stampEditable ? (
            /* Mudar de página é mudar `visual.page`, não arrastar: a posição é normalizada POR
               PÁGINA, então trazer o carimbo para cá preserva x/y e troca só a folha. Clicar é um
               gesto COMPLETO — desenha e grava no mesmo ato, sem debounce a esperar. */
            <Button
              variant="outline"
              size="sm"
              disabled={visualLocked}
              onClick={() => {
                const moved = { ...visual, page: currentPage };
                onVisualChange(moved);
                onVisualCommit(moved);
              }}
            >
              Trazer o carimbo para esta página
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
